import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  IconButton,
  Button,
  Alert,
  Chip,
  Paper,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Lock as LockIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { VariableService } from '../services/variableService';
import { useVariableStore } from '../store/variableStore';
import { CATEGORIAS, initialVariable } from '../types/variable';
import type { VariablePlanilla } from '../types/variable';

const parseDate = (dateStr: string): Date => {
  const s = dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00';
  return new Date(s);
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const d = parseDate(dateStr);
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const VariablesPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { variables, loading, error, setVariables, setLoading, setError, updateVariable, addVariable, removeVariable } =
    useVariableStore();
  const [editValues, setEditValues] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState<Record<number, boolean>>({});
  const [addOpen, setAddOpen] = useState(false);
  const [newVar, setNewVar] = useState<VariablePlanilla>({ ...initialVariable });
  const [expanded, setExpanded] = useState<string[]>(Object.keys(CATEGORIAS));

  // History dialog state
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyVariable, setHistoryVariable] = useState<VariablePlanilla | null>(null);
  const [historyData, setHistoryData] = useState<VariablePlanilla[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [newHistoryValue, setNewHistoryValue] = useState('');
  const [newHistoryDate, setNewHistoryDate] = useState<Date | null>(new Date());

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await VariableService.getAll();
      setVariables(data);
      const vals: Record<number, string> = {};
      data.forEach((v) => {
        vals[v.idVariable] = v.valor;
      });
      setEditValues(vals);
    } catch {
      setError('Error al cargar las variables');
    } finally {
      setLoading(false);
    }
  }, [setVariables, setLoading, setError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleValueChange = (id: number, value: string) => {
    setEditValues((prev) => ({ ...prev, [id]: value }));
  };

  const handleSave = async (variable: VariablePlanilla) => {
    const newValue = editValues[variable.idVariable];
    if (newValue === variable.valor) return;

    setSaving((prev) => ({ ...prev, [variable.idVariable]: true }));
    try {
      const updated = await VariableService.save({ ...variable, valor: newValue });
      updateVariable({ ...variable, ...updated, valor: newValue });
    } catch {
      setError(`Error al guardar ${variable.codigo}`);
      setEditValues((prev) => ({ ...prev, [variable.idVariable]: variable.valor }));
    } finally {
      setSaving((prev) => ({ ...prev, [variable.idVariable]: false }));
    }
  };

  const handleDelete = async (variable: VariablePlanilla) => {
    if (variable.esSistema) return;
    if (!confirm(`¿Eliminar la variable "${variable.descripcion}"?`)) return;

    try {
      await VariableService.delete(variable.idVariable);
      removeVariable(variable.idVariable);
    } catch {
      setError(`Error al eliminar ${variable.codigo}`);
    }
  };

  const handleAdd = async () => {
    try {
      const created = await VariableService.save(newVar);
      addVariable(created);
      setEditValues((prev) => ({ ...prev, [created.idVariable]: created.valor }));
      setAddOpen(false);
      setNewVar({ ...initialVariable });
    } catch {
      setError('Error al crear la variable');
    }
  };

  const handleToggleExpand = (cat: string) => {
    setExpanded((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  // --- History handlers ---

  const handleOpenHistory = async (variable: VariablePlanilla) => {
    setHistoryVariable(variable);
    setHistoryOpen(true);
    setHistoryLoading(true);
    setNewHistoryValue('');
    setNewHistoryDate(new Date());
    try {
      const data = await VariableService.getHistorial(variable.codigo);
      setHistoryData(data);
    } catch {
      setError(`Error al cargar historial de ${variable.codigo}`);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleAddHistoryValue = async () => {
    if (!historyVariable || !newHistoryDate || !newHistoryValue) return;
    try {
      const newEntry: VariablePlanilla = {
        ...historyVariable,
        idVariable: 0,
        valor: newHistoryValue,
        vigenciaDesde: newHistoryDate.toISOString().split('T')[0],
      };
      await VariableService.save(newEntry);
      const data = await VariableService.getHistorial(historyVariable.codigo);
      setHistoryData(data);
      await loadData();
      setNewHistoryValue('');
      setNewHistoryDate(new Date());
    } catch {
      setError('Error al agregar valor al historial');
    }
  };

  const handleDeleteHistoryEntry = async (id: number) => {
    if (!confirm('¿Eliminar esta entrada del historial?')) return;
    try {
      await VariableService.delete(id);
      setHistoryData((prev) => prev.filter((h) => h.idVariable !== id));
      await loadData();
    } catch {
      setError('Error al eliminar entrada del historial');
    }
  };

  const groupedVariables = Object.keys(CATEGORIAS).map((cat) => ({
    categoria: cat,
    label: CATEGORIAS[cat],
    items: variables.filter((v) => v.categoria === cat).sort((a, b) => a.orden - b.orden),
  }));

  if (loading && variables.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: isMobile ? 1 : 3, maxWidth: 1000, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Variables de Planilla
        </Typography>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} size={isMobile ? 'small' : 'medium'}>
          Nueva Variable
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Valores configurables usados en los cálculos de planilla, beneficios y aportes.
        Cada variable mantiene un historial de valores por fecha de vigencia.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {groupedVariables.map((group) => (
        <Accordion
          key={group.categoria}
          expanded={expanded.includes(group.categoria)}
          onChange={() => handleToggleExpand(group.categoria)}
          sx={{ mb: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography fontWeight="bold">{group.label}</Typography>
              <Chip label={group.items.length} size="small" variant="outlined" />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: isMobile ? 1 : 2 }}>
            {group.items.length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                No hay variables en esta categoría
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {group.items.map((variable) => (
                  <Paper
                    key={variable.idVariable}
                    variant="outlined"
                    sx={{
                      p: isMobile ? 1.5 : 2,
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      alignItems: isMobile ? 'stretch' : 'center',
                      gap: isMobile ? 1 : 2,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {variable.esSistema && (
                          <Tooltip title="Variable del sistema">
                            <LockIcon fontSize="small" color="action" />
                          </Tooltip>
                        )}
                        <Typography variant="body2" fontWeight="bold" noWrap>
                          {variable.codigo}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {variable.descripcion}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        Desde: {formatDate(variable.vigenciaDesde)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: isMobile ? 'auto' : 250 }}>
                      <TextField
                        size="small"
                        value={editValues[variable.idVariable] ?? variable.valor}
                        onChange={(e) => handleValueChange(variable.idVariable, e.target.value)}
                        onBlur={() => handleSave(variable)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSave(variable);
                        }}
                        type={variable.tipo === 'int' ? 'number' : variable.tipo === 'decimal' ? 'number' : 'text'}
                        inputProps={{
                          step: variable.tipo === 'decimal' ? '0.01' : '1',
                          style: { textAlign: 'right' },
                        }}
                        sx={{ width: isMobile ? '100%' : 130 }}
                      />
                      <Tooltip title="Guardar">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleSave(variable)}
                            disabled={saving[variable.idVariable] || editValues[variable.idVariable] === variable.valor}
                          >
                            {saving[variable.idVariable] ? <CircularProgress size={18} /> : <SaveIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Historial de valores">
                        <IconButton size="small" onClick={() => handleOpenHistory(variable)}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!variable.esSistema && (
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => handleDelete(variable)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Dialog para agregar variable */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva Variable</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            select
            label="Categoría"
            value={newVar.categoria}
            onChange={(e) => setNewVar((p) => ({ ...p, categoria: e.target.value }))}
            size="small"
            fullWidth
          >
            {Object.entries(CATEGORIAS).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Código"
            value={newVar.codigo}
            onChange={(e) => setNewVar((p) => ({ ...p, codigo: e.target.value.toUpperCase().replace(/\s/g, '_') }))}
            size="small"
            fullWidth
            helperText="Ej: MI_VARIABLE_CUSTOM"
          />
          <TextField
            label="Descripción"
            value={newVar.descripcion}
            onChange={(e) => setNewVar((p) => ({ ...p, descripcion: e.target.value }))}
            size="small"
            fullWidth
          />
          <TextField
            select
            label="Tipo"
            value={newVar.tipo}
            onChange={(e) => setNewVar((p) => ({ ...p, tipo: e.target.value }))}
            size="small"
            fullWidth
          >
            <MenuItem value="decimal">Decimal</MenuItem>
            <MenuItem value="int">Entero</MenuItem>
            <MenuItem value="bool">Booleano</MenuItem>
          </TextField>
          <TextField
            label="Valor"
            value={newVar.valor}
            onChange={(e) => setNewVar((p) => ({ ...p, valor: e.target.value }))}
            size="small"
            fullWidth
          />
          <DatePicker
            label="Vigencia desde"
            value={newVar.vigenciaDesde ? parseDate(newVar.vigenciaDesde) : new Date()}
            onChange={(date) =>
              setNewVar((p) => ({
                ...p,
                vigenciaDesde: date ? date.toISOString().split('T')[0] : '',
              }))
            }
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
          <TextField
            label="Orden"
            type="number"
            value={newVar.orden}
            onChange={(e) => setNewVar((p) => ({ ...p, orden: parseInt(e.target.value) || 0 }))}
            size="small"
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!newVar.codigo || !newVar.descripcion || !newVar.valor}
          >
            Crear
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de historial */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Historial: {historyVariable?.codigo}
          <Typography variant="body2" color="text.secondary">
            {historyVariable?.descripcion}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* Form to add new historical value */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, mt: 1, alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
            <DatePicker
              label="Vigencia desde"
              value={newHistoryDate}
              onChange={setNewHistoryDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
            />
            <TextField
              label="Nuevo valor"
              value={newHistoryValue}
              onChange={(e) => setNewHistoryValue(e.target.value)}
              size="small"
              sx={{ width: isMobile ? '100%' : 130 }}
            />
            <Button
              variant="contained"
              onClick={handleAddHistoryValue}
              disabled={!newHistoryValue || !newHistoryDate}
              size="small"
              sx={{ minWidth: 90, mt: isMobile ? 0 : 0.5 }}
            >
              Agregar
            </Button>
          </Box>

          {/* History entries */}
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {historyData.map((entry, idx) => (
                <Paper
                  key={entry.idVariable}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: idx === 0 ? 'action.hover' : 'transparent',
                  }}
                >
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {formatDate(entry.vigenciaDesde)}
                      </Typography>
                      {idx === 0 && (
                        <Chip label="Vigente" size="small" color="primary" variant="outlined" />
                      )}
                    </Box>
                    <Typography variant="body1" sx={{ mt: 0.25 }}>
                      Valor: <strong>{entry.valor}</strong>
                    </Typography>
                  </Box>
                  {historyData.length > 1 && (
                    <Tooltip title="Eliminar entrada">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteHistoryEntry(entry.idVariable)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Paper>
              ))}
              {historyData.length === 0 && (
                <Typography color="text.secondary" variant="body2" sx={{ py: 2, textAlign: 'center' }}>
                  Sin historial
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VariablesPage;
